// The platform console's sign-in chrome (spec 15 §4), and the HOST branch that
// gives the area its own root.
//
// The console is NOT the office: it has no tenant, no module nav, no global
// search and no tenant switcher, because none of those exist on
// `admin.<apex>`. Two frames serve it:
//
//   * THIS one, for the signed-out flow (sign-in, invite, enrolment, password
//     reset) and for the session check itself: a white top bar naming the
//     product, the environment chip and the locale control, over a centered
//     card, the shape the design system's UX-5 slice prescribes for auth.
//   * console/console-shell.tsx, for everything under `/console` once the
//     guard has said yes: the grouped rail, the top bar with the signed-in
//     staff member and sign-out, the screen (D-626). The `/console` layout
//     route draws it, so `AdminChrome` hands those paths straight through.
//
// `AdminChrome` is the branch `RootLayout` takes on the admin host. Any path
// that is not one of the console's own renders the console's 404 rather than
// the office's: the office 404 offers "Go to Home", and /home on this host is
// an office screen wired to tenant endpoints that answer 404 here.

import type { ReactNode } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/button";
import { LocaleSwitcher } from "../../components/locale-switcher";
import { StatusChip } from "../../components/status-chip";
import { Toasts } from "../../components/toast";
import { isStagingHost } from "../../routes/shell-data";
import { isAdminOnlyPath } from "../../lib/host";
import "./admin.css";

/** Paths both apps answer on, each deciding by host at render time: `/` and
 * `/login` (routes/login.tsx), and the password-reset pair `/forgot-password`
 * and `/reset` (routes/forgot-password.tsx, routes/reset-password.tsx; spec 15
 * §4 S1a and S1b, D-587). The pair was missing here when S1a and S1b shipped,
 * so the console rendered its 404 under the right URL; M15-S15 caught it. */
const SHARED_CONSOLE_PATHS = ["/", "/login", "/forgot-password", "/reset"] as const;

/** Paths the console owns or shares with the tenant app. */
export function isConsolePath(pathname: string): boolean {
  return (
    (SHARED_CONSOLE_PATHS as readonly string[]).includes(pathname) || isAdminOnlyPath(pathname)
  );
}

export function AdminShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const staging = isStagingHost(window.location.hostname);
  return (
    <div className="admin-app" data-testid="admin-app">
      <header className="admin-top">
        <span className="admin-brand">
          <span className="admin-brand-mk" aria-hidden="true">
            A
          </span>
          <span className="admin-brand-name">{t("admin.brand")}</span>
          <span className="admin-brand-sub">{t("admin.console")}</span>
        </span>
        <div className="admin-top-right">
          {staging ? <StatusChip variant="neutral">{t("nav.envStaging")}</StatusChip> : null}
          <LocaleSwitcher className="ds-select" />
        </div>
      </header>
      <main className="admin-main">{children}</main>
      <Toasts />
    </div>
  );
}

/**
 * The centered auth card: one h1-equivalent title, one lead sentence, the form.
 * Nothing in this area renders above `--text-lg` (§1 rule 4).
 */
export function AdminAuthCard({
  title,
  lead,
  children,
  className,
  ...rest
}: {
  title: ReactNode;
  lead?: ReactNode;
  children: ReactNode;
  /** Only ever a width modifier (`admin-card-wide`) — never colour or type. */
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <section
      className={className === undefined ? "admin-card" : `admin-card ${className}`}
      {...rest}
    >
      <h1 className="admin-card-title">{title}</h1>
      {lead !== undefined ? <p className="admin-card-lead">{lead}</p> : null}
      {children}
    </section>
  );
}

function AdminNotFoundScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <AdminAuthCard title={t("admin.notFound.title")} data-testid="admin-not-found">
      <p className="admin-card-lead">{t("admin.notFound.body")}</p>
      <div className="admin-actions">
        <Button
          variant="primary"
          onClick={() => {
            void navigate({ to: "/login" });
          }}
        >
          {t("admin.notFound.cta")}
        </Button>
      </div>
    </AdminAuthCard>
  );
}

/** `/console` and everything under it: the signed-in console, whose layout
 * route draws its own frame (console/console-shell.tsx). */
export function isSignedInConsolePath(pathname: string): boolean {
  return pathname === "/console" || pathname.startsWith("/console/");
}

/** The whole admin host: the signed-in console draws its own shell, the other
 * console paths render inside the sign-in frame, and anything else renders
 * the console's own 404 (never the office's). */
export function AdminChrome() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (isSignedInConsolePath(pathname)) return <Outlet />;
  return <AdminShell>{isConsolePath(pathname) ? <Outlet /> : <AdminNotFoundScreen />}</AdminShell>;
}
